import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Pathname } from '@src/types/Browsing';
import { CollectionView } from 'common';
import { history } from '@src/browserHistory';
import { useQueryCollections, setQueryCollections } from '@src/queries/collections/useQueryCollections';
import { Footer } from '@src/components/Footer';
import { ADALONG_COLORS } from '@src/styles';
import NavBar from '../../components/NavBar';
import { CollectionsHeader } from './List/CollectionsListHeader';
import { style } from 'typestyle';
import CollectionsListContent, { CollectionCardPreview } from '@src/pages/Collection/List/CollectionsListContent';
import { useMutationCollection } from '@src/queries/collections/useMutationCollection';
import * as appHelpers from '@src/services/helpers/appHelpers';
import { useAtomValue } from 'jotai';
import { userAtom } from '@src/state/userAtom';
import { SortBy } from './List/consts';
import CategorySection from './List/CategorySection';
import { useCollectionFilters } from '@src/hooks/Collection/useCollectionFilters';
import { useCollectionsListState } from '@src/hooks/Collection/useCollectionsListState';
import { useCollectionDragAndDrop } from '@src/hooks/Collection/useCollectionDragAndDrop';
import { useCategoryCollapse } from '@src/hooks/Collection/useCategoryCollapse';
import { CollectionsListState } from '@src/types/Collection';

const TAB_LABELS: Record<'collections' | 'admin', string> = {
  collections: 'Collections',
  admin: 'Admin collections',
};

export function Collections() {
  const { data: collections = [], isLoading, refetch: refetchCollections } = useQueryCollections();
  const user = useAtomValue(userAtom);
  const [sortBy, setSortBy] = useState<SortBy>('date');
  const [filter, setFilter] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'collections' | 'admin'>('collections');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      pressDelay: 150,
      activationConstraint: { distance: 6 },
    })
  );

  const { mutateAsync: mutateCollection, error: errorUpdate, reset: resetMutateCollection } = useMutationCollection();

  const viewerId = user?._id ?? '';
  const isAdminUser = Boolean(user?.admin);

  useEffect(() => {
    if (!isAdminUser && activeTab === 'admin') {
      setActiveTab('collections');
    }
  }, [isAdminUser, activeTab]);

  const { displayedCollections, filteredCollections } = useCollectionFilters({
    collections,
    viewerId,
    isAdminUser,
    activeTab,
    filter,
  });

  const detailsSave = (collection: CollectionView, category: string) => {
    mutateCollection(
      {
        _id: collection._id,
        name: collection.name,
        description: collection.description,
        category,
      },
      {
        onSuccess: () => {
          // Intentionally silent on success to avoid redundant banners
        },
        onError: () => {
          appHelpers.addAlert({
            text: errorUpdate?.message
              ? `We could not update this collection: ${errorUpdate.message}`
              : 'We could not update this collection. Please try again.',
            type: 'danger',
          });
        },
      }
    );
  };

  const { collectionsList, setCollectionsList, visibleCollectionsByCategory, findCategoryForCollection } =
    useCollectionsListState({
      displayedCollections,
      filteredCollections,
      sortBy,
    });

  const persistCategoryChange = useCallback(
    (collection: CollectionView, destinationCategory: string) => {
      detailsSave(collection, destinationCategory);
      setQueryCollections(
        collections.map((current) =>
          current._id === collection._id ? { ...current, category: destinationCategory } : current
        )
      );
    },
    [collections, detailsSave]
  );

  const { isDragging, activeCollectionId, handleDragStart, handleDragEnd, handleDragCancel } =
    useCollectionDragAndDrop({
      collections,
      collectionsList,
      setCollectionsList,
      findCategoryForCollection,
      onPersistCategoryChange: persistCategoryChange,
    });

  const { collapsedCategories, toggleCategory } = useCategoryCollapse({
    collectionsList,
    visibleCollectionsByCategory,
    isDragging,
    isLoading,
  });

  const activeCollection = useMemo(
    () => collections.find((collection) => collection._id === activeCollectionId) ?? null,
    [activeCollectionId, collections]
  );

  return (
    <div id='collections' style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: ADALONG_COLORS.SMOOTH_BLACK }}>
      <div style={{ backgroundColor: ADALONG_COLORS.DARK_BLACK }}>
        <NavBar active={Pathname.collect} />
      </div>
      <div className='page-content' style={{ flex: 1, overflow: 'hidden' }}>
        <CollectionsHeader
          list={displayedCollections}
          sortBy={sortBy}
          setSortBy={setSortBy}
          filter={filter}
          setFilter={setFilter}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isAdminUser={isAdminUser}
          isLoading={isLoading}
          onCollectionCreated={(collection) => history.push(`/collections/${collection._id}`)}
        />
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className={collectionsListClass}>
            {collectionsList.map((cat) => {
              const isCollapsed = collapsedCategories.has(cat.name);
              const collectionsForCategory = visibleCollectionsByCategory.get(cat.name) ?? [];
              return (
                <CategorySection
                  key={cat.name}
                  name={cat.name}
                  isCollapsed={isCollapsed}
                  itemCount={collectionsForCategory.length}
                  isLoading={isLoading}
                  onToggle={toggleCategory}
                >
                  <CollectionsListContent
                    category={cat.name}
                    list={collectionsForCategory}
                    isLoading={isLoading}
                    onCollectionsChange={refetchCollections}
                  />
                </CategorySection>
              );
            })}
          </div>
          <DragOverlay>
            {activeCollection ? (
              <CollectionCardPreview collection={activeCollection} className={dragOverlayPreviewClass} />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      <Footer />
    </div>
  );
}

const padding = '15px';
const collectionsListClass = style({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: `0 ${padding}`,
  overflowY: 'auto',
  $nest: {
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: 3,
    },
  },
});

const dragOverlayPreviewClass = style({
  opacity: 0.9,
  cursor: 'grabbing',
  filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.25))',
});
